import { Injectable, Injector } from '@angular/core';
import { WorkOrderObjectModel, WorkLineModel } from './work-order-object.model';
import { WorkOrderObjectsHierarchyControllerService } from '@app/generated/service';
import { CustomerSiteAdministrationControllerService } from '@app/generated/api';
import { ProjectControllerService } from '@app/generated/api/projectController.service';
import { ProjectDto, WorkOrderDetailsDto, UserDto } from '@app/generated/model';
import { ChangeLogEvent } from '@app/core/db/change-log';
import { AbstractWorkOrderDataService } from '@app/work-order/abstract-work-order-data.service';
import { UserService } from '@app/core/services/user.service';
import { BehaviorSubject, combineLatest, firstValueFrom, interval, Observable, of } from 'rxjs';
import { LocationAdministrationControllerService } from '@app/generated/api/locationAdministrationController.service';
import { SiteDto } from '@app/admin/models/site-dto';
import { catchError, debounce, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { UpdateWorkOrderObjectProgress } from '@app/core/model/update-work-order-object-progress';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { FeatureFlagKey } from '@dwo/enums';

import { CustomerSiteDto } from '@app/generated/model/customerSiteDto';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderObjectService extends AbstractWorkOrderDataService<WorkOrderObjectModel[]> {
  private selectedWorkLines = new Set<WorkLineModel>();
  private selectedObjects = new Set<WorkOrderObjectModel>();
  private projectsByUser = new Map<string, Map<string, ProjectDto>>();
  private loadingProjectsByUser = new Map<string, Observable<Map<string, ProjectDto>>>();
  private customerSiteCodeBySiteNo = new Map<string, string>();
  private loadingCustomerSiteCodeBySiteNo = new Map<string, Observable<string>>();
  private customerSitesByWorkOrder = new Map<string, Observable<CustomerSiteDto[]>>();

  private selectedWorkLines$ = new BehaviorSubject(new Set<WorkLineModel>());
  private selectedObjects$ = new BehaviorSubject(new Set<WorkOrderObjectModel>());
  private objects$ = new BehaviorSubject<WorkOrderObjectModel[]>([]);

  constructor(
    private objectControllerService: WorkOrderObjectsHierarchyControllerService,
    private projectControllerService: ProjectControllerService,
    private customerSiteAdministrationControllerService: CustomerSiteAdministrationControllerService,
    private locationAdministrationControllerService: LocationAdministrationControllerService,
    private userService: UserService,
    private featureFlagService: FeatureFlagService,
    injector: Injector,
  ) {
    super(injector, 'OBJECTS');
  }

  getCompletionLink(projectId: string, objectId: string, tagNumber = ''): Observable<string> {
    if (!projectId || !objectId) {
      return of('');
    }

    return this.getAuthorizedProjectById(projectId).pipe(
      switchMap((project) => {
        const completionSystem = this.getCompletionSystemValue(project);
        const normalizedTagNumber = tagNumber.toString().trim();
        const parent = this.getParent() as WorkOrderDetailsDto;

        if (completionSystem === 'COMPLETION') {
          return of(
            `https://completion.akersolutions.com/project/${encodeURIComponent(projectId)}/object/${encodeURIComponent(
              objectId,
            )}`,
          );
        } else if (
          completionSystem === 'PROCOSYS' &&
          this.featureFlagService.isEnabled(FeatureFlagKey.EnableProcosysLink, projectId)
        ) {
          const siteId = parent?.siteNo || '';
          const activityProject = parent?.activity?.r1 || '';

          if (!activityProject || !siteId || !normalizedTagNumber) {
            return of('');
          }

          // Match siteNo to CustomerSiteDto.siteCode, use siteName for PROCOSYS link
          return this.getCustomerSitesForCurrentWorkOrder().pipe(
            map((customerSites: CustomerSiteDto[]) => {
              const matchedSite = (customerSites || []).find((site) => site.siteCode === siteId);
              const siteName = matchedSite?.siteName?.trim();
              if (!siteName) {
                return '';
              }
              return `https://procosys.equinor.com/${encodeURIComponent(
                siteName,
              )}/Link/TAG?project=${encodeURIComponent(activityProject)}&TAGNo=${encodeURIComponent(
                normalizedTagNumber,
              )}`;
            }),
          );
        } else {
          // Placeholder for future completion systems
          return of('');
        }
      }),
    );
  }

  private getCustomerSiteNameForSiteNo(siteNo: string): Observable<string> {
    const normalizedSiteNo = siteNo.toUpperCase();

    const cachedSiteCode = this.customerSiteCodeBySiteNo.get(normalizedSiteNo);
    if (cachedSiteCode !== undefined) {
      return of(cachedSiteCode);
    }

    const loadingSiteCode = this.loadingCustomerSiteCodeBySiteNo.get(normalizedSiteNo);
    if (loadingSiteCode) {
      return loadingSiteCode;
    }

    const siteCodeRequest$ = this.getCustomerSitesForCurrentWorkOrder().pipe(
      map((customerSites) => {
        const matchedSite = (customerSites || []).find((customerSite) => {
          const mappedSiteCode = (customerSite.siteCode || '').toString().trim().toUpperCase();
          const mappedSiteName = (customerSite.siteName || '').toString().trim().toUpperCase();
          return mappedSiteCode === normalizedSiteNo || mappedSiteName === normalizedSiteNo;
        });

        return (matchedSite?.siteName || '').toString().trim();
      }),
      catchError(() => of('')),
      tap((siteCode) => {
        this.customerSiteCodeBySiteNo.set(normalizedSiteNo, siteCode);
        this.loadingCustomerSiteCodeBySiteNo.delete(normalizedSiteNo);
      }),
      shareReplay(1),
    );

    this.loadingCustomerSiteCodeBySiteNo.set(normalizedSiteNo, siteCodeRequest$);
    return siteCodeRequest$;
  }

  private getCustomerSitesForCurrentWorkOrder(): Observable<CustomerSiteDto[]> {
    const cacheKey = this.workOrderId || ((this.getParent() as WorkOrderDetailsDto | null)?.id ?? '');

    if (!cacheKey) {
      return this.customerSiteAdministrationControllerService
        .getCustomerSites()
        .pipe(map((customerSites) => customerSites ?? []));
    }

    const cachedCustomerSites = this.customerSitesByWorkOrder.get(cacheKey);
    if (cachedCustomerSites) {
      return cachedCustomerSites;
    }

    const customerSitesRequest$ = this.customerSiteAdministrationControllerService.getCustomerSites().pipe(
      map((customerSites) => customerSites ?? []),
      catchError(() => {
        this.customerSitesByWorkOrder.delete(cacheKey);
        return of([]);
      }),
      shareReplay(1),
    );

    this.customerSitesByWorkOrder.set(cacheKey, customerSitesRequest$);
    return customerSitesRequest$;
  }

  getCompletionSystemName(projectId: string): Observable<string> {
    if (!projectId) {
      return of('');
    }

    return this.getAuthorizedProjectById(projectId).pipe(
      map((project) => this.toPascalCase(this.getCompletionSystemValue(project))),
    );
  }

  private getAuthorizedProjectById(projectId: string): Observable<ProjectDto | undefined> {
    const currentUser = this.userService.get();

    if (currentUser?.username) {
      return this.getAuthorizedProjectsForUser(currentUser.username).pipe(
        map((projectsById) => projectsById.get(projectId)),
      );
    }

    return this.userService.getObserver().pipe(
      filter((user: UserDto | null): user is UserDto => !!user?.username),
      take(1),
      switchMap((user) => this.getAuthorizedProjectsForUser(user.username!)),
      map((projectsById) => projectsById.get(projectId)),
    );
  }

  private getAuthorizedProjectsForUser(username: string): Observable<Map<string, ProjectDto>> {
    const cachedProjects = this.projectsByUser.get(username);
    if (cachedProjects) {
      return of(cachedProjects);
    }

    const loadingProjects = this.loadingProjectsByUser.get(username);
    if (loadingProjects) {
      return loadingProjects;
    }

    const projectsRequest$ = this.projectControllerService.getAuthorizedProjects(username).pipe(
      map((projects) => {
        const projectsById = new Map<string, ProjectDto>();
        (projects || []).forEach((project) => {
          if (project?.id) {
            projectsById.set(project.id, project);
          }
        });
        return projectsById;
      }),
      tap((projectsById) => {
        this.projectsByUser.set(username, projectsById);
        this.loadingProjectsByUser.delete(username);
      }),
      shareReplay(1),
    );

    this.loadingProjectsByUser.set(username, projectsRequest$);
    return projectsRequest$;
  }

  private getCompletionSystemValue(project: ProjectDto | undefined): string {
    const completionValue =
      project?.completionSystem ??
      (project as (ProjectDto & { completionType?: string }) | undefined)?.completionType ??
      '';

    return completionValue.toString().trim().toUpperCase();
  }

  private toPascalCase(value: string): string {
    return value
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((part) => !!part)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');
  }

  getObjects() {
    return this.objects$;
  }

  getSelectedObjects() {
    // Use debounce to limit the stream as the CRUD updates everything one by one (Should be refactored to support batch)
    return this.selectedObjects$.pipe(debounce(() => interval(100)));
  }

  hasSelectedItems() {
    return combineLatest([this.selectedWorkLines$, this.selectedObjects$]).pipe(
      map((both) => !!both[0].size || !!both[1].size),
    );
  }

  clearSelected() {
    this.get().forEach((o) => this.removeSelectedObject(o));
  }

  selectAll() {
    this.get().forEach((o) => this.addSelectedObject(o));
  }

  addSelectedObject(object: WorkOrderObjectModel) {
    const hasWeeklyProgress = (this.getParent() as WorkOrderDetailsDto).showWeeklyProgress;
    const workLines = object.workLines ?? [];
    const children = object.children ?? [];

    object.isSelected = true;
    this.selectedObjects.add(object);

    workLines
      .filter((c) => (hasWeeklyProgress ? c.lockedProgress !== 100 : true))
      .forEach((wl) => this.addSelectedWorkLine(wl));
    children
      .filter((c) => (hasWeeklyProgress ? c.lockedProgress !== 100 : true))
      .forEach((c) => this.addSelectedObject(c));

    this.selectedObjects$.next(this.selectedObjects);
  }

  removeSelectedObject(object: WorkOrderObjectModel) {
    object.isSelected = false;
    this.selectedObjects.delete(object);
    (object.workLines ?? []).forEach((wl) => this.removeSelectedWorkLine(wl));
    (object.children ?? []).forEach((c) => this.removeSelectedObject(c));
    this.selectedObjects$.next(this.selectedObjects);
  }

  addSelectedWorkLine(workLine: WorkLineModel) {
    workLine.isSelected = true;
    this.selectedWorkLines.add(workLine);
    this.selectedWorkLines$.next(this.selectedWorkLines);
  }

  removeSelectedWorkLine(workLine: WorkLineModel) {
    workLine.isSelected = false;
    this.selectedWorkLines.delete(workLine);
    this.selectedWorkLines$.next(this.selectedWorkLines);
  }

  getMaxProgressAsObservable() {
    return this.selectedWorkLines$.pipe(
      debounce(() => interval(100)),
      map((workLines) => Math.max(...[...workLines.values()].map((c) => c.progress ?? 0), 0)),
    );
  }

  getMaxLockedProgressAsObservable() {
    return this.selectedWorkLines$.pipe(
      debounce(() => interval(100)),
      map((workLines) => Math.max(...[...this.selectedWorkLines.values()].map((w) => w.lockedProgress), 0)),
    );
  }

  getMaxProgress() {
    return Math.max(...[...this.selectedWorkLines.values()].map((c) => c.progress ?? 0), 0);
  }

  getMaxLockedProgress() {
    return Math.max(...[...this.selectedWorkLines.values()].map((w) => w.lockedProgress), 0);
  }

  protected rehydrate(): void {
    this.offlineData.workOrderDataItems
      .where({ workOrderId: this.workOrderId, type: 'OBJECTS' })
      .first()
      .then((item) => {
        const data = item?.data ?? [];
        this.objects$.next(data);
        this.setData(data);
      });
  }

  protected load(): void {
    this.objectControllerService
      .getObjects1(this.workOrderId)
      .pipe(map((hierarchy) => (hierarchy || []).map((dto) => WorkOrderObjectModel.fromDto(dto))))
      .subscribe({
        next: (objects) => {
          this.objects$.next(objects);
          this.setData(objects);
        },
        error: (error) => {
          console.warn(
            `[OBJECTS] Failed to load object hierarchy for ${this.workOrderId}. Falling back to offline data.`,
            error,
          );
          void this.handleLoadFailure([], error);
        },
      });
  }

  public async download() {
    try {
      const hierarchy = await firstValueFrom(this.objectControllerService.getObjects1(this.workOrderId));
      const objects = (hierarchy || []).map((dto) => WorkOrderObjectModel.fromDto(dto));
      this.objects$.next(objects);
      this.setData(objects);
    } catch (error) {
      console.warn('[OBJECTS] Failed to load object hierarchy for offline download. Saving empty object list.', error);
      this.objects$.next([]);
      this.setData([]);
    }

    await super.download();
  }

  updateWorkOrderObjectProgress(objectStatusId: string | null, progress: number, resetStatus: boolean) {
    const workOrderId = (this.getParent() as WorkOrderDetailsDto).id;

    if (!workOrderId) {
      return Promise.resolve('');
    }

    const payload = {
      workOrderObjectIds: [...this.selectedObjects.values()].map((obj) => obj.id),
      workLineIds: [...this.selectedWorkLines.values()].map((wo) => wo.id),
      workOrderId,
      progress,
      resetStatus,
    } as UpdateWorkOrderObjectProgress;

    if (objectStatusId) {
      payload.objectStatusId = objectStatusId;
    }

    return this.changeLogService.publish('WO_UPDATE_OBJ_PROGRESS', payload, [workOrderId]);
  }

  protected apply(objects: WorkOrderObjectModel[], event: ChangeLogEvent) {
    switch (event.type) {
      case 'WO_UPDATE_OBJ_PROGRESS': {
        this.refreshModel(objects, event.content as UpdateWorkOrderObjectProgress);
        this.objects$.next(objects);
      }
    }
  }

  private refreshModel(objects: WorkOrderObjectModel[], change: UpdateWorkOrderObjectProgress) {
    objects.forEach((workOrderObject) => {
      this.refreshWorkOrderObject(objects, workOrderObject, change);
    });
  }

  private refreshWorkOrderObject(
    objects: WorkOrderObjectModel[],
    workOrderObject: WorkOrderObjectModel,
    change: UpdateWorkOrderObjectProgress,
  ) {
    (workOrderObject.children ?? []).forEach((childWorkOrderObject) => {
      this.refreshWorkOrderObject(objects, childWorkOrderObject, change);
    });

    // only refresh the changed workOrderObject
    if ((change.workOrderObjectIds ?? []).some((id) => id === workOrderObject.id)) {
      if (change.objectStatusId || change.resetStatus) {
        workOrderObject.objectStatusId = change.objectStatusId;
      }
      workOrderObject.progress = change.progress;
    }

    for (const workLine of (workOrderObject.workLines ?? []).filter((wl) =>
      (change.workLineIds ?? []).some((id) => wl.id === id),
    )) {
      workLine.progress = change.progress;
    }
  }
}
